import Seo from 'common/components/seo.component';
import { APP_NAME, APP_URL } from 'config/constants/api.constants';
import { RouteName } from 'config/constants/route.constants';
import { PaymentStatus } from 'config/constants/order.constants';
import { OrderTranscription } from 'config/constants/transcription.constant';
import OrderService from 'config/services/order.service';
import { PaymentStatusResponse } from 'config/types/order.type';
import { SeoOpenGraphType } from 'config/types/common.type';
import CartUtils from 'config/utils/cart.util';
import NavigationUtils from 'config/utils/navigation.util';
import OrderUtils from 'config/utils/order.utils';
import { SnackbarUtilities } from 'config/utils/snackbar.util';
import PurchaseAbortedResult from 'order/components/aborted-payment.component';
import PurchaseTimeoutResult from 'order/components/timeout-payment.component';
import PurchaseResultContainer from 'order/containers/purchase-result.container';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/index';
import { OrderSlice } from 'store/slices/order.slice';
import { PaymentSlice } from 'store/slices/payment.slice';
import { PurchaseSlice } from 'store/slices/purchase.slice';
import PurchaseResultSkeleton from './purchase-result-skeleton';

// TODO: esto hay que moverlo a los utils
const PAYMENT_STATUS_BACKOFF_MS = [0, 1000, 2000, 3000, 5000, 5000, 5000, 5000];

const openGraphTags: SeoOpenGraphType[] = [
  {
    property: 'title',
    content: OrderTranscription.PURCHASE_RESULT,
  },
  {
    property: 'url',
    content: `${APP_URL}${RouteName.PURCHASE_CHECKOUT}`,
  },
  {
    property: 'site_name',
    content: APP_NAME,
  },
  {
    property: 'description',
    content: OrderTranscription.PURCHASE_RESULT,
  },
];

const PurchaseResult: React.FC = () => {
  const dispatch = useDispatch();
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [pollState, setPollState] = useState<'checking' | 'paid' | 'failed' | 'aborted' | 'expired' | 'unresolved'>('checking');
  const {
    auth: { isAuthenticated },
    user,
    order: { isFetching: orderFetching, entryCode: storeEntryCode },
    purchase: { item: purchaseItem },
  } = useSelector((state: RootState) => state);
  const { oc } = NavigationUtils.handlePurchaseResultQueryParams();
  const finalEntryCode = oc || storeEntryCode || '';

  const handleGetOrderDetails = async (orderCode: string) => {
    dispatch(OrderSlice.fetchingRefresh(true));
    try {
      const orderDetails = await OrderService.getOrderDetails({ entryCode: orderCode });
      dispatch(PurchaseSlice.refreshItem(orderDetails));
    } catch (_e) {
      SnackbarUtilities.error(OrderTranscription.ERROR_GET_ORDER_DETAILS);
    } finally {
      dispatch(OrderSlice.fetchingRefresh(false));
    }
  };

  const handlePaid = async (status: PaymentStatusResponse & { state: 'PAID' }) => {
    await handleGetOrderDetails(status.orderCode || status.entryCode);
    setPollState('paid');
    await CartUtils.clearCartAfterPurchase();
    dispatch(OrderSlice.resetIdempotencyKey());
    dispatch(PaymentSlice.resetPaymentStore());
  };

  const pollPaymentStatus = async (entryCode: string, attempt = 0) => {
    if (!isAuthenticated || !entryCode) return;

    try {
      const status = await OrderService.getPaymentStatus(entryCode);

      if (status.state === 'PAID') {
        await handlePaid(status);
        return;
      }

      if (status.state === 'FAILED') {
        setPollState('failed');
        return;
      }

      if (status.state === 'ABORTED' || status.state === 'EXPIRED') {
        OrderUtils.rotateIdempotencyKey();
        setPollState(status.state === 'ABORTED' ? 'aborted' : 'expired');
        return;
      }

      if (attempt >= PAYMENT_STATUS_BACKOFF_MS.length - 1) {
        setPollState('unresolved');
        return;
      }

      pollTimeoutRef.current = setTimeout(
        () => {
          pollPaymentStatus(entryCode, attempt + 1);
        },
        PAYMENT_STATUS_BACKOFF_MS[attempt + 1],
      );
    } catch (_e) {
      if (attempt >= PAYMENT_STATUS_BACKOFF_MS.length - 1) {
        setPollState('unresolved');
        return;
      }
      pollTimeoutRef.current = setTimeout(
        () => {
          pollPaymentStatus(entryCode, attempt + 1);
        },
        PAYMENT_STATUS_BACKOFF_MS[attempt + 1],
      );
    }
  };

  useEffect(() => {
    if (finalEntryCode && isAuthenticated) {
      pollPaymentStatus(finalEntryCode, 0);
    }

    return () => {
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, [finalEntryCode, isAuthenticated]);

  const isResultPending = orderFetching || pollState === 'checking' || (pollState === 'paid' && !purchaseItem);

  return (
    <React.Fragment>
      <Seo title={OrderTranscription.PURCHASE_RESULT} openGraphTags={openGraphTags} />

      {isResultPending ?
        <PurchaseResultSkeleton />
      : pollState === 'aborted' ?
        <PurchaseAbortedResult user={user.userProfile} />
      : pollState === 'expired' ?
        <PurchaseTimeoutResult user={user.userProfile} />
      : <PurchaseResultContainer
          user={user.userProfile}
          confirmedPurchaseResponse={
            pollState === 'paid' ? { paymentStatus: PaymentStatus.SUCCESS, paymentMethodSlug: 'webpay' }
            : pollState === 'failed' ?
              { paymentStatus: PaymentStatus.FAILED, paymentMethodSlug: 'webpay' }
            : undefined
          }
          entryCode={finalEntryCode}
          purchase={purchaseItem}
          pollState={pollState}
        />
      }
    </React.Fragment>
  );
};

export default PurchaseResult;
